import ElitAiSimulationsPage from "pages/elitai/simulations/ElitAiSimulationsPage";
import React, { LazyExoticComponent, Suspense, lazy } from "react";
import { Navigate, useLocation, useRoutes } from "react-router-dom";

import LoadingScreen from "@/components/LoadingScreen";
import PermissionGuardExample from "@/components/PermissionGuardExample";
import { PAGE_PERMISSIONS } from "@/constants/permissions";
import LogoOnlyLayout from "@/layouts/LogoOnlyLayout";
import DashboardLayout from "@/layouts/dashboard";
import CalculatorsPage from "@/pages/dashboard/calc-panel/CalculatorsPage";
import PermissionsPage from "@/pages/dashboard/permissions/PermissionsPage";
import { PromptsForm, PromptsList } from "@/pages/dashboard/prompts";
import PrpSessionFeedbackPage from "@/pages/dashboard/prpSessionFeedback/PrpSessionFeedbackPage";
import { ProtectedRoute } from "@/providers/hooks/useProtectedRoute";

const Loadable = (Component: LazyExoticComponent<any>) => () => {
    const { pathname } = useLocation();

    return (
        <Suspense fallback={<LoadingScreen isDashboard={pathname.includes("/dashboard")} />}>
            <Component />
        </Suspense>
    );
};

export default function Router() {
    return useRoutes([
        {
            path: "/",
            element: <Navigate to="/dashboard" replace />,
        },
        {
            path: "/dashboard",
            element: (
                <ProtectedRoute>
                    <DashboardLayout />
                </ProtectedRoute>
            ),
            children: [
                { element: <Navigate to="/dashboard/home" replace />, index: true },
                {
                    path: "home",
                    element: (
                        <ProtectedRoute>
                            <HomePage />
                        </ProtectedRoute>
                    ),
                },
                {
                    path: "users",
                    children: [
                        {
                            element: (
                                <ProtectedRoute permissions={PAGE_PERMISSIONS.USERS}>
                                    <UsersListPage />
                                </ProtectedRoute>
                            ),
                            index: true,
                        },
                    ],
                },
                {
                    path: "permissions",
                    children: [
                        {
                            element: (
                                <ProtectedRoute permissions={PAGE_PERMISSIONS.PERMISSIONS}>
                                    <PermissionsPage />
                                </ProtectedRoute>
                            ),
                            index: true,
                        },
                    ],
                },
                {
                    path: "patients",
                    children: [
                        {
                            element: (
                                <ProtectedRoute permissions={PAGE_PERMISSIONS.USERS}>
                                    <PatientsListPage />
                                </ProtectedRoute>
                            ),
                            index: true,
                        },
                        {
                            path: ":id",
                            element: (
                                <ProtectedRoute permissions={PAGE_PERMISSIONS.USERS}>
                                    <PatientDetailsPage />
                                </ProtectedRoute>
                            ),
                        },
                    ],
                },
                {
                    path: "strings",
                    children: [
                        {
                            element: (
                                <ProtectedRoute permissions={PAGE_PERMISSIONS.STRINGS}>
                                    <StringsListPage />
                                </ProtectedRoute>
                            ),
                            index: true,
                        },
                    ],
                },
                {
                    path: "feedbacks",
                    children: [
                        {
                            element: (
                                <ProtectedRoute permissions={PAGE_PERMISSIONS.FEEDBACKS}>
                                    <PrpSessionFeedbackPage />
                                </ProtectedRoute>
                            ),
                            index: true,
                        },
                    ],
                },
                {
                    path: "calc",
                    children: [
                        {
                            element: (
                                <ProtectedRoute permissions={PAGE_PERMISSIONS.CALCULATORS}>
                                    <CalculatorsPage />
                                </ProtectedRoute>
                            ),
                            index: true,
                        },
                        {
                            path: ":id",
                            element: (
                                <ProtectedRoute permissions={PAGE_PERMISSIONS.CALCULATORS}>
                                    <CalculatorsPage />
                                </ProtectedRoute>
                            ),
                        },
                    ],
                },
                {
                    path: "promotions",
                    children: [
                        {
                            element: (
                                <ProtectedRoute permissions={PAGE_PERMISSIONS.PROMOTIONS}>
                                    <PromotionsManagerPage />
                                </ProtectedRoute>
                            ),
                            index: true,
                        },
                    ],
                },
                {
                    path: "extra-treatments",
                    children: [
                        {
                            element: (
                                <ProtectedRoute permissions={PAGE_PERMISSIONS.EXTRA_TREATMENTS}>
                                    <ExtraTreatmentsPage />
                                </ProtectedRoute>
                            ),
                            index: true,
                        },
                    ],
                },
                {
                    path: "machine-caller",
                    children: [
                        { element: <Navigate to="/dashboard/machine-caller/results" replace />, index: true },
                        {
                            path: "results",
                            element: (
                                <ProtectedRoute permissions={PAGE_PERMISSIONS.MACHINE_CALLER}>
                                    <MachineCallerListPage />
                                </ProtectedRoute>
                            ),
                        },
                    ],
                },
                {
                    path: "offers",
                    children: [
                        {
                            element: (
                                <ProtectedRoute permissions={PAGE_PERMISSIONS.OFFERS}>
                                    <OffersListPage />
                                </ProtectedRoute>
                            ),
                            index: true,
                        },
                    ],
                },
                {
                    path: "templates",
                    children: [
                        {
                            element: (
                                <ProtectedRoute permissions={PAGE_PERMISSIONS.TEMPLATES}>
                                    <TemplateListPage />
                                </ProtectedRoute>
                            ),
                            index: true,
                        },
                        {
                            path: "new",
                            element: (
                                <ProtectedRoute permissions={PAGE_PERMISSIONS.TEMPLATE_EDIT}>
                                    <TemplateForm />
                                </ProtectedRoute>
                            ),
                        },
                        {
                            path: ":id/edit",
                            element: (
                                <ProtectedRoute permissions={PAGE_PERMISSIONS.TEMPLATE_EDIT}>
                                    <TemplateForm />
                                </ProtectedRoute>
                            ),
                        },
                        {
                            path: ":id/clone",
                            element: (
                                <ProtectedRoute permissions={PAGE_PERMISSIONS.TEMPLATE_EDIT}>
                                    <TemplateForm />
                                </ProtectedRoute>
                            ),
                        },
                        {
                            path: ":id/version",
                            element: (
                                <ProtectedRoute permissions={PAGE_PERMISSIONS.TEMPLATE_EDIT}>
                                    <TemplateForm />
                                </ProtectedRoute>
                            ),
                        },
                    ],
                },
                {
                    path: "elitai",
                    children: [
                        {
                            path: "simulations",
                            element: (
                                <ProtectedRoute permissions={PAGE_PERMISSIONS.ELITAI_SIMULATIONS}>
                                    <ElitAiSimulationsPage />
                                </ProtectedRoute>
                            ),
                            index: true,
                        },
                    ],
                },
                {
                    path: "prompts",
                    children: [
                        {
                            element: (
                                <ProtectedRoute permissions={PAGE_PERMISSIONS.PERMISSIONS}>
                                    <PromptsList />
                                </ProtectedRoute>
                            ),
                            index: true,
                        },
                        {
                            path: "form",
                            element: (
                                <ProtectedRoute permissions={PAGE_PERMISSIONS.PERMISSIONS}>
                                    <PromptsForm />
                                </ProtectedRoute>
                            ),
                        },
                        {
                            path: "form/:id",
                            element: (
                                <ProtectedRoute permissions={PAGE_PERMISSIONS.PERMISSIONS}>
                                    <PromptsForm />
                                </ProtectedRoute>
                            ),
                        },
                    ],
                },
                {
                    path: "tutorials",
                    children: [
                        {
                            element: (
                                <ProtectedRoute permissions={PAGE_PERMISSIONS.TUTORIALS}>
                                    <TutorialsListPage />
                                </ProtectedRoute>
                            ),
                            index: true,
                        },
                    ],
                },
                {
                    path: "shortlinks",
                    children: [
                        {
                            element: (
                                <ProtectedRoute permissions={PAGE_PERMISSIONS.SHORTLINKS}>
                                    <ShortlinksListPage />
                                </ProtectedRoute>
                            ),
                            index: true,
                        },
                    ],
                },
                {
                    path: "sections",
                    children: [
                        {
                            element: (
                                <ProtectedRoute permissions={PAGE_PERMISSIONS.SECTIONS}>
                                    <SectionListPage />
                                </ProtectedRoute>
                            ),
                            index: true,
                        },
                        {
                            path: "editSection/:id",
                            element: (
                                <ProtectedRoute permissions={PAGE_PERMISSIONS.SECTION_EDIT}>
                                    <SectionForm />
                                </ProtectedRoute>
                            ),
                        },
                    ],
                },
                {
                    path: "prp-pos",
                    children: [
                        {
                            element: (
                                <ProtectedRoute>
                                    <PointOfSalePage />
                                </ProtectedRoute>
                            ),
                            index: true,
                        },
                    ],
                },
                {
                    path: "hp-pos",
                    children: [
                        {
                            element: (
                                <ProtectedRoute>
                                    <PointOfSalePage />
                                </ProtectedRoute>
                            ),
                            index: true,
                        },
                    ],
                },
                {
                    path: "test-auth-error",
                    element: (
                        <ProtectedRoute>
                            <TestAuthErrorPage />
                        </ProtectedRoute>
                    ),
                },
                {
                    path: "permission-guard-example",
                    element: (
                        <ProtectedRoute>
                            <PermissionGuardExample />
                        </ProtectedRoute>
                    ),
                },
            ],
        },
        {
            path: "/dashboard/offers/create/:id",
            element: (
                <ProtectedRoute permissions={PAGE_PERMISSIONS.OFFERS}>
                    <CreateOfferPage />
                </ProtectedRoute>
            ),
        },
        {
            path: "/dashboard/offers/:id",
            element: (
                <ProtectedRoute permissions={PAGE_PERMISSIONS.OFFERS}>
                    <ViewOfferPage />
                </ProtectedRoute>
            ),
        },
        {
            path: "*",
            element: <LogoOnlyLayout />,
            children: [
                { path: "404", element: <NotFound /> },
                { path: "*", element: <Navigate to="/404" replace /> },
            ],
        },
        { path: "*", element: <Navigate to="/404" replace /> },
    ]);
}

// Offers
const CreateOfferPage = Loadable(lazy(() => import("../pages/dashboard/offers/CreateOfferPage")));
const ViewOfferPage = Loadable(lazy(() => import("../pages/dashboard/offers/ViewOfferPage")));

// Dashboard
const HomePage = Loadable(lazy(() => import("../pages/dashboard/home/HomePage")));
const UsersListPage = Loadable(lazy(() => import("../pages/dashboard/users/users-list-page")));
const PatientsListPage = Loadable(lazy(() => import("../pages/dashboard/patients/PatientsListPage")));
const PatientDetailsPage = Loadable(lazy(() => import("../pages/dashboard/patients/PatientDetailsPage")));
const StringsListPage = Loadable(lazy(() => import("../pages/dashboard/strings/StringsListPage")));
const PromotionsManagerPage = Loadable(lazy(() => import("../pages/dashboard/promotions/PromotionsManagerPage")));
const ExtraTreatmentsPage = Loadable(lazy(() => import("../pages/dashboard/extra-treatments/ExtraTreatmentsPage")));
const MachineCallerListPage = Loadable(lazy(() => import("../pages/dashboard/twilio/MachineCallerListPage")));
const OffersListPage = Loadable(lazy(() => import("../pages/dashboard/offers/OffersListPage")));
const PointOfSalePage = Loadable(lazy(() => import("../pages/dashboard/payment/PointOfSale")));
const NotFound = Loadable(lazy(() => import("../pages/Page404")));
const TemplateListPage = Loadable(lazy(() => import("../pages/dashboard/templates/templateListPageModified")));
const TemplateForm = Loadable(lazy(() => import("../pages/dashboard/templates/TemplateForm")));
const SectionListPage = Loadable(lazy(() => import("../pages/dashboard/sections/SectionsListPage")));
const SectionForm = Loadable(lazy(() => import("../pages/dashboard/sections/SectionForm")));
const TutorialsListPage = Loadable(lazy(() => import("../pages/dashboard/tutorials/TutorialsListPage")));
const ShortlinksListPage = Loadable(lazy(() => import("../pages/dashboard/shortlinks/ShortlinksListPage")));
const TestAuthErrorPage = Loadable(lazy(() => import("../pages/test-auth-error")));
